'use client';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type MouseEvent,
} from 'react';
import { useLocale } from 'use-intl';
import { useMedia } from '@ssv-website/hooks';
import { MessagePlugin } from 'tdesign-react';
import styles from './BannerSection.module.css';
import { getConferenceMobileBrandImageUrl } from '../brandMedia.helpers';
import type { ConferenceSite } from '../types';
import iconAddress from '../icon-address.svg';
import iconTime from '../icon-time.svg';
import {
  clearConferenceSignupPendingAction,
  isConferenceSignupSurveyUrl,
  redirectConferenceSignupLogin,
  requestConferenceSignupRedirect,
  saveConferenceSignupPendingAction,
  type ConferenceSignupButtonType,
} from '../api';

interface BannerSectionProps {
  data?: any;
  brands?: any[];
  brandsLoaded?: boolean;
  enableServerVideoPoster?: boolean;
  enableServerMobileImage?: boolean;
  site?: ConferenceSite;
  languageSwitch?: React.ReactNode;
}

interface BrandItem {
  fileType?: string;
  contentUrl: string;
  videoCover?: string;
  videoPoster?: string;
}

type WindowWithConferenceBrandMedia = Window & {
  __SSV_CONFERENCE_BRAND_MEDIA_URLS?: Set<string>;
};

const VIDEO_EXT = /\.(mp4|mov|webm|ogg)(\?.*)?$/i;
const IMAGE_EXT = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i;
const THEME_CONTENT_ACTIVE_CLASS = 'themeContentActive';

// 品牌营销轮播：每张图停留 3000ms，fade 过渡 300ms。
const MOBILE_BRAND_INTERVAL_MS = 3000;

const getBrandMediaType = (item: BrandItem) => {
  const fileType = item.fileType?.toLowerCase();
  if (fileType === 'video' || fileType === 'image') {
    return fileType;
  }
  if (VIDEO_EXT.test(item.contentUrl)) {
    return 'video';
  }
  if (IMAGE_EXT.test(item.contentUrl)) {
    return 'image';
  }
  return 'image';
};

const isVideoItem = (item: BrandItem) => getBrandMediaType(item) === 'video';

const getBrandIdentity = (item?: BrandItem | null) => item?.contentUrl || '';

const getInitialPcVideoItem = (brands?: BrandItem[]) =>
  (Array.isArray(brands) ? brands : []).find(
    (item) => item.contentUrl && isVideoItem(item)
  ) || null;

const getInitialMobileImageItems = (brands?: BrandItem[]) =>
  (Array.isArray(brands) ? brands : []).filter(
    (item) => item.contentUrl && !isVideoItem(item)
  );

const normalizeBrandMediaUrl = (url: string) => {
  try {
    return new URL(url, window.location.origin).href;
  } catch (error) {
    return url;
  }
};

const shuffleBrandItems = (arr: BrandItem[]) => {
  const list = [...arr];
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
};

const buildPlaybackQueue = (
  items: BrandItem[],
  previousItem?: BrandItem | null
) => {
  const list = shuffleBrandItems(items);
  if (list.length <= 1) {
    return list;
  }

  const previousIdentity = getBrandIdentity(previousItem);
  if (!previousIdentity || getBrandIdentity(list[0]) !== previousIdentity) {
    return list;
  }

  const nextIndex = list.findIndex(
    (item) => getBrandIdentity(item) !== previousIdentity
  );
  if (nextIndex > 0) {
    [list[0], list[nextIndex]] = [list[nextIndex], list[0]];
  }
  return list;
};

const splitTitleByFirstLineBreak = (title: string) => {
  const index = title.indexOf('\\n');
  if (index === -1) return [title];
  return [title.slice(0, index), title.slice(index + 2)];
};

/**
 * Banner 头图组件 - 2026 S大会
 *
 * 参照本地 demo：整段 banner 占据 2.2 屏，内部 sticky stage 100vh。
 * "AI for Good" 写死不变；主题/副标题/时间/地址/按钮文案走 data 接口。
 */
export default function BannerSection({
  data,
  brands,
  brandsLoaded = true,
  enableServerVideoPoster = false,
  enableServerMobileImage = false,
  site = 'zh',
  languageSwitch,
}: BannerSectionProps) {
  const locale = useLocale();
  const isMobile = useMedia('mobile');
  // useMedia 在 hydration 首次渲染时尚未读取 matchMedia，避免此时按 PC 初始化视频队列。
  const isMediaResolved = typeof isMobile === 'boolean';
  const isZh = locale === 'zh';
  const isDomesticSite = site !== 'en';

  const [redirectingButton, setRedirectingButton] = useState<
    ConferenceSignupButtonType | ''
  >('');
  // 首条 PC 视频由服务端和客户端以相同规则确定，首个 HTML 可直接输出首帧图。
  const [currentItem, setCurrentItem] = useState<BrandItem | null>(() =>
    enableServerVideoPoster ? getInitialPcVideoItem(brands) : null
  );
  // 移动端首图由服务端按固定顺序选择，保证首个 HTML 和水合首帧一致；
  // 后续轮播仍在客户端洗牌，且图片节点不会卸载，避免切换露出蓝色画布。
  const [mobileSlides, setMobileSlides] = useState<BrandItem[]>(() =>
    enableServerMobileImage ? getInitialMobileImageItems(brands) : []
  );
  const [mobileActiveIdentity, setMobileActiveIdentityState] = useState(() =>
    getBrandIdentity(
      enableServerMobileImage ? getInitialMobileImageItems(brands)[0] : null
    )
  );
  const [mediaVersion, setMediaVersion] = useState(0);
  const [videoDisplayReadyKey, setVideoDisplayReadyKey] = useState('');
  const [videoSourceIds, setVideoSourceIds] = useState<string[]>([]);
  const [nextVideoIdentity, setNextVideoIdentity] = useState('');
  const currentItemRef = useRef<BrandItem | null>(
    enableServerVideoPoster ? getInitialPcVideoItem(brands) : null
  );
  const queueRef = useRef<BrandItem[]>([]);
  const mobileTimerRef = useRef<NodeJS.Timeout | null>(null);
  const mobilePlayedRef = useRef<Set<string>>(new Set());
  const mobileSlideImgRefs = useRef<Map<string, HTMLImageElement | null>>(
    new Map()
  );
  const mobileActiveIdentityRef = useRef<string>(
    getBrandIdentity(
      enableServerMobileImage ? getInitialMobileImageItems(brands)[0] : null
    )
  );
  const mobileServerInitialSlidesPendingRef = useRef(
    enableServerMobileImage && getInitialMobileImageItems(brands).length > 0
  );
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const preloadTokenRef = useRef(0);
  const preloadImageRef = useRef<HTMLImageElement | null>(null);
  const videoPoolRefs = useRef<Map<string, HTMLVideoElement | null>>(new Map());
  const pendingNextVideoIdentityRef = useRef('');
  const failedVideoIdentitiesRef = useRef<Set<string>>(new Set());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLHeadingElement>(null);
  const infoGroupRef = useRef<HTMLDivElement>(null);
  const scrollHintRef = useRef<HTMLDivElement>(null);
  const displayBrands = useMemo(
    () =>
      (Array.isArray(brands) ? brands : []).filter((item: BrandItem) => {
        if (!item.contentUrl) return false;
        return isMobile ? !isVideoItem(item) : isVideoItem(item);
      }),
    [brands, isMobile]
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      preloadTokenRef.current += 1;
      preloadImageRef.current = null;
    },
    []
  );

  const applyBrandItem = useCallback((item: BrandItem | null) => {
    const applyItem = () => {
      currentItemRef.current = item;
      setCurrentItem(item);
      setMediaVersion((version) => version + 1);
    };

    if (item && !isVideoItem(item)) {
      // 先让下一张图完成解码，再替换当前媒体，避免切换瞬间露出底层蓝色画布。
      // 解码完成后必须保留 image 引用：canvas 绘制 effect 需要从 preloadImageRef 同步取出已解码的位图。
      const preloadToken = preloadTokenRef.current + 1;
      preloadTokenRef.current = preloadToken;
      const image = new window.Image();
      preloadImageRef.current = image;
      image.onload = () => {
        void image
          .decode()
          .catch(() => undefined)
          .finally(() => {
            if (preloadTokenRef.current !== preloadToken) {
              return;
            }
            applyItem();
          });
      };
      image.onerror = () => {
        if (preloadTokenRef.current !== preloadToken) {
          return;
        }
        preloadImageRef.current = null;
        applyItem();
      };
      image.src = item.contentUrl;
      return;
    }

    preloadTokenRef.current += 1;
    preloadImageRef.current = null;
    applyItem();
  }, []);

  const prepareVideoSources = useCallback(
    (current: BrandItem | null, next: BrandItem | null) => {
      const sourceIds = [current, next]
        .filter((item): item is BrandItem => Boolean(item && isVideoItem(item)))
        .map(getBrandIdentity)
        .filter(Boolean);

      if (sourceIds.length) {
        // 已挂载的视频节点不再移除 src，轮播回环时可复用浏览器已有缓冲。
        setVideoSourceIds((currentIds) =>
          Array.from(new Set([...currentIds, ...sourceIds]))
        );
      }
      setNextVideoIdentity(getBrandIdentity(next));
    },
    []
  );

  const getNextVideoItem = useCallback(() => {
    const availableItems = displayBrands.filter(
      (item) => !failedVideoIdentitiesRef.current.has(getBrandIdentity(item))
    );
    if (!availableItems.length) {
      return null;
    }

    const currentIdentity = getBrandIdentity(currentItemRef.current);
    if (availableItems.length === 1) {
      return getBrandIdentity(availableItems[0]) === currentIdentity
        ? null
        : availableItems[0];
    }

    if (queueRef.current.length === 0) {
      queueRef.current = buildPlaybackQueue(
        availableItems,
        currentItemRef.current
      );
    }
    while (
      queueRef.current.length > 0 &&
      failedVideoIdentitiesRef.current.has(
        getBrandIdentity(queueRef.current[0])
      )
    ) {
      queueRef.current.shift();
    }
    return queueRef.current[0] ?? null;
  }, [displayBrands]);

  const activateNextVideo = useCallback(() => {
    const current = currentItemRef.current;
    const next = getNextVideoItem();
    if (!current || !next) {
      return;
    }

    const nextIdentity = getBrandIdentity(next);
    prepareVideoSources(current, next);
    const nextVideo = videoPoolRefs.current.get(nextIdentity);
    if (!nextVideo || nextVideo.readyState < 2) {
      // 当前视频停在最后一帧，等下一条真正可播后再切换，避免黑屏或空白。
      pendingNextVideoIdentityRef.current = nextIdentity;
      return;
    }

    pendingNextVideoIdentityRef.current = '';
    queueRef.current.shift();
    applyBrandItem(next);
  }, [applyBrandItem, getNextVideoItem, prepareVideoSources]);

  const pickNext = useCallback(() => {
    clearTimer();
    const all = displayBrands;
    if (all.length === 0) {
      queueRef.current = [];
      applyBrandItem(null);
      return;
    }
    // 只有 1 张 brand 时不做切换，避免 1 秒等待 + 蓝底闪烁
    if (all.length === 1) {
      return;
    }

    if (
      !isMobile &&
      currentItemRef.current &&
      isVideoItem(currentItemRef.current)
    ) {
      activateNextVideo();
      return;
    }

    if (queueRef.current.length === 0) {
      queueRef.current = buildPlaybackQueue(all, currentItemRef.current);
    }
    const next = queueRef.current.shift() ?? null;
    applyBrandItem(next);
  }, [displayBrands, clearTimer, applyBrandItem, activateNextVideo, isMobile]);

  useEffect(() => {
    if (!isMediaResolved) {
      return;
    }

    const valid = displayBrands;
    // 登记品牌营销媒体地址，供 RUM 定向过滤该轮播产生的资源测速上报。
    (
      window as WindowWithConferenceBrandMedia
    ).__SSV_CONFERENCE_BRAND_MEDIA_URLS = new Set(
      valid.map((item: BrandItem) => normalizeBrandMediaUrl(item.contentUrl))
    );
    clearTimer();
    if (valid.length === 0) {
      queueRef.current = [];
      applyBrandItem(null);
      return;
    }
    const currentIdentity = getBrandIdentity(currentItemRef.current);
    const hasCurrentItem = valid.some(
      (item) => getBrandIdentity(item) === currentIdentity
    );
    const list = buildPlaybackQueue(
      valid,
      hasCurrentItem ? currentItemRef.current : null
    );
    if (hasCurrentItem) {
      // 首条已在服务端 HTML 中渲染，只补齐后续轮播队列，避免 hydration 后重置 Banner。
      queueRef.current = list.filter(
        (item) => getBrandIdentity(item) !== currentIdentity
      );
      return;
    }
    const first = list.shift() ?? null;
    queueRef.current = list;
    applyBrandItem(first);
  }, [displayBrands, clearTimer, applyBrandItem, isMediaResolved]);

  useEffect(() => {
    if (!isMediaResolved) {
      return;
    }

    if (!isMobile || displayBrands.length === 0) {
      if (mobileTimerRef.current) {
        clearTimeout(mobileTimerRef.current);
        mobileTimerRef.current = null;
      }
      queueRef.current = [];
      mobilePlayedRef.current = new Set();
      mobileActiveIdentityRef.current = '';
      mobileServerInitialSlidesPendingRef.current = false;
      setMobileSlides([]);
      return;
    }

    if (mobileServerInitialSlidesPendingRef.current) {
      const currentIdentity = mobileActiveIdentityRef.current;
      const current = displayBrands.find(
        (item) => getBrandIdentity(item) === currentIdentity
      );
      if (current) {
        queueRef.current = shuffleBrandItems(
          displayBrands.filter(
            (item) => getBrandIdentity(item) !== currentIdentity
          )
        );
        mobilePlayedRef.current = new Set([currentIdentity]);
        mobileServerInitialSlidesPendingRef.current = false;
        return;
      }
      mobileServerInitialSlidesPendingRef.current = false;
    }

    // 初始化：洗牌取首张作为当前图，剩余进队列。
    const shuffled = shuffleBrandItems(displayBrands);
    const first = shuffled[0] ?? null;
    queueRef.current = shuffled.slice(1);
    mobilePlayedRef.current = new Set(first ? [getBrandIdentity(first)] : []);
    mobileActiveIdentityRef.current = first ? getBrandIdentity(first) : '';
    setMobileActiveIdentityState(mobileActiveIdentityRef.current);
    setMobileSlides(shuffled);
  }, [displayBrands, isMobile, isMediaResolved]);

  // 移动端 brand 自动播放：自调度 setTimeout，每轮自己安排下一轮，不依赖 React
  // effect 重跑来重启 timer，避免 cleanup 时机干扰导致循环中断。
  // 移动端 brand 自动播放：每张停留 3000ms 切换 active identity，通过 ref 控制
  // img 的 opacity 触发 CSS transition。React 始终渲染同一组 img 不卸载，避免
  // 浏览器重新解析 src 导致画面空白。
  useEffect(() => {
    if (!isMobile || mobileSlides.length <= 1) {
      return;
    }

    const applyOpacity = (activeId: string) => {
      mobileSlideImgRefs.current.forEach((img, id) => {
        if (!img) return;
        img.style.opacity = id === activeId ? '1' : '0';
      });
    };

    applyOpacity(mobileActiveIdentityRef.current);

    const scheduleNext = () => {
      mobileTimerRef.current = setTimeout(() => {
        // 队列空了 → 重新洗牌补队，并整体替换 children 让 React 重渲染。
        if (queueRef.current.length === 0) {
          const reshuffled = shuffleBrandItems(displayBrands);
          queueRef.current = reshuffled.slice(1);
          const firstNewId = reshuffled[0]
            ? getBrandIdentity(reshuffled[0])
            : '';
          mobilePlayedRef.current = new Set(firstNewId ? [firstNewId] : []);
          mobileActiveIdentityRef.current = firstNewId;
          setMobileActiveIdentityState(firstNewId);
          setMobileSlides(reshuffled);
          requestAnimationFrame(() => applyOpacity(firstNewId));
          scheduleNext();
          return;
        }
        const next = queueRef.current.shift() ?? null;
        if (!next) {
          scheduleNext();
          return;
        }
        mobileActiveIdentityRef.current = getBrandIdentity(next);
        mobilePlayedRef.current.add(getBrandIdentity(next));
        setMobileActiveIdentityState(mobileActiveIdentityRef.current);
        requestAnimationFrame(() =>
          applyOpacity(mobileActiveIdentityRef.current)
        );
        scheduleNext();
      }, MOBILE_BRAND_INTERVAL_MS);
    };

    scheduleNext();

    return () => {
      if (mobileTimerRef.current) {
        clearTimeout(mobileTimerRef.current);
        mobileTimerRef.current = null;
      }
    };
  }, [mobileSlides, isMobile, displayBrands]);

  // 当前视频开始播放后，只预缓冲队列中的下一条，避免首屏并发下载全部视频。
  useEffect(() => {
    if (
      !isMediaResolved ||
      !currentItem ||
      !isVideoItem(currentItem) ||
      isMobile
    ) {
      return;
    }
    setVideoDisplayReadyKey('');
    prepareVideoSources(currentItem, getNextVideoItem());
  }, [
    currentItem,
    getNextVideoItem,
    isMobile,
    isMediaResolved,
    mediaVersion,
    prepareVideoSources,
  ]);
  useEffect(() => {
    // 只有 1 张 brand 时不做切换；视频本身有 ended 循环，所以这里只需排除图片定时器
    if (
      !isMediaResolved ||
      isMobile ||
      !currentItem ||
      isVideoItem(currentItem)
    ) {
      return;
    }
    const brandCount = displayBrands.length;
    if (brandCount <= 1) {
      clearTimer();
      return;
    }
    clearTimer();
    timerRef.current = setTimeout(() => {
      pickNext();
    }, 3000);
    return clearTimer;
  }, [
    isMobile,
    isMediaResolved,
    currentItem,
    mediaVersion,
    pickNext,
    clearTimer,
    displayBrands,
  ]);

  const onEndedRef = useRef(pickNext);
  onEndedRef.current = pickNext;

  useEffect(() => {
    const identity = getBrandIdentity(currentItem);
    const el = videoPoolRefs.current.get(identity);
    const currentMediaKey = currentItem
      ? `${getBrandIdentity(currentItem)}-${mediaVersion}`
      : '';
    if (
      !isMediaResolved ||
      !identity ||
      !el ||
      !currentItem ||
      !isVideoItem(currentItem)
    ) {
      return;
    }

    const markVideoDisplayReady = () => {
      if (!currentMediaKey) {
        return;
      }
      setVideoDisplayReadyKey((current) =>
        current === currentMediaKey ? current : currentMediaKey
      );
    };

    const playVideo = (logPrefix: string) => {
      void el.play().catch((error) => {
        console.warn(logPrefix, error);
      });
    };

    const handleLoaded = () => {
      markVideoDisplayReady();
      playVideo('[BannerSection] video play failed after load');
    };

    if (el.readyState >= 2) {
      markVideoDisplayReady();
      playVideo('[BannerSection] video play failed');
    } else {
      el.addEventListener('loadeddata', handleLoaded, { once: true });
    }

    return () => {
      el.removeEventListener('loadeddata', handleLoaded);
    };
  }, [currentItem, isMobile, isMediaResolved, mediaVersion, videoSourceIds]);

  const handlePooledVideoCanPlay = useCallback(
    (identity: string) => {
      if (pendingNextVideoIdentityRef.current === identity) {
        activateNextVideo();
      }
    },
    [activateNextVideo]
  );

  const handlePooledVideoError = useCallback(
    (identity: string) => {
      failedVideoIdentitiesRef.current.add(identity);
      queueRef.current = queueRef.current.filter(
        (item) => getBrandIdentity(item) !== identity
      );

      if (pendingNextVideoIdentityRef.current === identity) {
        pendingNextVideoIdentityRef.current = '';
        activateNextVideo();
        return;
      }
      if (getBrandIdentity(currentItemRef.current) === identity) {
        activateNextVideo();
      }
    },
    [activateNextVideo]
  );

  // 把预加载好的图片位图直接绘制到 canvas，跳过 <img> 渲染管线的"上传 GPU → 合成"步骤，
  // 避免"先蓝后图"现象。仅对图片生效；视频仍走 <video> 元素。
  useEffect(() => {
    if (
      !isMediaResolved ||
      isMobile ||
      !currentItem ||
      isVideoItem(currentItem)
    ) {
      return;
    }
    const canvas = canvasRef.current;
    const img = preloadImageRef.current;
    if (!canvas || !img || !img.complete || !img.naturalWidth) {
      return;
    }

    const draw = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // 让 canvas 物理像素匹配容器尺寸 × devicePixelRatio，绘制更清晰
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const cssW = Math.max(1, Math.floor(rect.width));
      const cssH = Math.max(1, Math.floor(rect.height));
      const targetW = cssW * dpr;
      const targetH = cssH * dpr;
      if (canvas.width !== targetW) canvas.width = targetW;
      if (canvas.height !== targetH) canvas.height = targetH;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // 等比缩放、right center 对齐（与原 background-size: cover / position: right center 一致）
      const imgAspect = img.naturalWidth / img.naturalHeight;
      const boxAspect = targetW / targetH;
      let drawW: number;
      let drawH: number;
      if (imgAspect > boxAspect) {
        drawH = targetH;
        drawW = drawH * imgAspect;
      } else {
        drawW = targetW;
        drawH = drawW / imgAspect;
      }
      const offsetX = targetW - drawW;
      const offsetY = (targetH - drawH) / 2;
      ctx.drawImage(img, offsetX, offsetY, drawW, drawH);
    };

    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [currentItem, mediaVersion, isMobile, isMediaResolved]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const headline = headlineRef.current;
    const infoGroup = infoGroupRef.current;
    const scrollHint = scrollHintRef.current;
    if (!scrollEl || !headline || !infoGroup) {
      return;
    }

    const clamp = (value: number, min: number, max: number) =>
      Math.min(Math.max(value, min), max);
    const mapClamp = (value: number, start: number, end: number) =>
      clamp((value - start) / (end - start), 0, 1);
    const easeOut = (value: number) => 1 - Math.pow(1 - value, 3);

    const applyScrollState = () => {
      if (isMobile) {
        scrollEl.classList.remove(THEME_CONTENT_ACTIVE_CLASS);
        headline.style.transform = '';
        headline.style.opacity = '';
        headline.style.filter = '';
        headline.style.webkitFilter = '';
        infoGroup.style.transform = '';
        if (scrollHint) scrollHint.style.opacity = '';
        return;
      }

      const rect = scrollEl.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const total = rect.height - viewportHeight;
      const raw = total > 0 ? clamp(-rect.top / total, 0, 1) : 0;

      // 与 demo 保持一致：全部动画在 0~0.4 进度内完成。
      const progress = mapClamp(raw, 0, 0.4);
      const eased = easeOut(progress);
      scrollEl.classList.toggle(THEME_CONTENT_ACTIVE_CLASS, progress > 0);

      const tx = eased * viewportWidth * 0.85;
      const tyPercent = -50 - eased * 140;
      const scale = 1 + eased * 1.4;
      const blur = progress * 22;
      const opacity = 1 - mapClamp(progress, 0.15, 1);
      const filter = `blur(${blur.toFixed(1)}px)`;

      headline.style.transform = `translate(${tx.toFixed(
        1
      )}px, ${tyPercent}%) scale(${scale})`;
      headline.style.opacity = opacity.toFixed(3);
      headline.style.filter = filter;
      headline.style.webkitFilter = filter;

      const infoTy = -eased * viewportHeight * 0.78;
      infoGroup.style.transform = `translate3d(0, ${infoTy}px, 0)`;

      if (scrollHint) {
        scrollHint.style.opacity = (1 - mapClamp(raw, 0, 0.06)).toFixed(3);
      }
    };

    let ticking = false;
    const handleScroll = () => {
      if (ticking) {
        return;
      }
      ticking = true;
      window.requestAnimationFrame(() => {
        applyScrollState();
        ticking = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', applyScrollState);
    applyScrollState();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', applyScrollState);
      headline.style.transform = '';
      headline.style.opacity = '';
      headline.style.filter = '';
      headline.style.webkitFilter = '';
      infoGroup.style.transform = '';
      scrollEl.classList.remove(THEME_CONTENT_ACTIVE_CLASS);
      if (scrollHint) scrollHint.style.opacity = '';
    };
  }, [isMobile]);

  // ---- 主题配置字段 ----

  const hasThemeData = Boolean(data);
  const titleCN = data?.titleCn || '';
  const titleEN = data?.titleEn || '';
  const subtitleCN = data?.subtitleCn || '';
  const subtitleEN = data?.subtitleEn || '';
  const eventDate = data?.startDate || '';
  const addressCN = data?.addressCn || '';
  const addressEN = data?.addressEn || '';
  const primaryBtnCN = data?.primaryBtnCn || '';
  const primaryBtnEN = data?.primaryBtnEn || '';
  const primaryBtnDomesticLink = data?.primaryBtnUrl || '';
  const primaryBtnOverseaLink = data?.primaryBtnOverseaUrl || '';
  const primaryBtnLink = isDomesticSite
    ? primaryBtnDomesticLink
    : primaryBtnOverseaLink;
  const secondaryBtnCN = data?.secondaryBtnCn || '';
  const secondaryBtnEN = data?.secondaryBtnEn || '';
  const secondaryBtnDomesticLink = data?.secondaryBtnUrl || '';
  const secondaryBtnOverseaLink = data?.secondaryBtnOverseaUrl || '';
  const secondaryBtnLink = isDomesticSite
    ? secondaryBtnDomesticLink
    : secondaryBtnOverseaLink;
  const themeCoverImage = data?.coverImage || '';
  const primaryButtonText = isZh ? primaryBtnCN : primaryBtnEN;
  const secondaryButtonText = isZh ? secondaryBtnCN : secondaryBtnEN;
  const addressText = isZh ? addressCN : addressEN;
  const showEventDate = hasThemeData && Boolean(eventDate);
  const showAddress = hasThemeData && Boolean(addressText);

  const primaryUsesControlledSignup =
    isDomesticSite && isConferenceSignupSurveyUrl(primaryBtnLink);
  const secondaryUsesControlledSignup =
    isDomesticSite && isConferenceSignupSurveyUrl(secondaryBtnLink);
  const primaryButtonHref = primaryUsesControlledSignup
    ? '#conference-signup-primary'
    : primaryBtnLink;
  const secondaryButtonHref = secondaryUsesControlledSignup
    ? '#conference-signup-secondary'
    : secondaryBtnLink;
  const showPrimaryButton =
    hasThemeData && Boolean(primaryButtonText) && Boolean(primaryBtnLink);
  const showSecondaryButton =
    hasThemeData && Boolean(secondaryButtonText) && Boolean(secondaryBtnLink);

  const handleConferenceSignupClick = async (
    event: MouseEvent<HTMLAnchorElement>,
    button: ConferenceSignupButtonType,
    link: string
  ) => {
    if (!isDomesticSite || !isConferenceSignupSurveyUrl(link)) {
      return;
    }
    event.preventDefault();
    if (redirectingButton) {
      return;
    }

    saveConferenceSignupPendingAction({
      surveyUrl: link,
      button,
      sourcePath: window.location.pathname + window.location.search,
      createdAt: Date.now(),
    });
    setRedirectingButton(button);

    const result = await requestConferenceSignupRedirect({ surveyUrl: link });
    if (result.kind === 'login') {
      redirectConferenceSignupLogin(site);
      return;
    }
    if (result.kind === 'ok') {
      clearConferenceSignupPendingAction();
      window.location.href = result.url;
      return;
    }

    clearConferenceSignupPendingAction();
    setRedirectingButton('');
    MessagePlugin.error({
      zIndex: 1000000,
      content: result.message,
    });
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return isZh ? '2026.02.03' : 'February 3, 2026';
    if (isZh) return dateStr.replace(/-/g, '.');
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const month = months[parseInt(parts[1], 10) - 1] || parts[1];
      return `${month} ${parseInt(parts[2], 10)}, ${parts[0]}`;
    }
    return dateStr;
  };

  const titleText = isZh ? titleCN : titleEN;
  const titleLines = splitTitleByFirstLineBreak(titleText);

  const hasMobileBrandImages =
    (isMobile || (!isMediaResolved && enableServerMobileImage)) &&
    mobileSlides.length > 0;
  // 主题背景仅在没有任何可展示的品牌素材时回退，避免移动端 SSR 同时输出品牌图和主题封面。
  const themeCoverStyle =
    brandsLoaded && !currentItem && !hasMobileBrandImages && themeCoverImage
      ? { backgroundImage: `url(${themeCoverImage})` }
      : undefined;
  const hasBrandContent = Boolean(currentItem);
  const hasButtons = showPrimaryButton || showSecondaryButton;
  const timeIconSize = isMobile ? 14 : 16;
  const addressIconWidth = isMobile ? 14 : 16;
  const addressIconHeight = isMobile ? 14 : 24;
  const isSingleBrand = displayBrands.length <= 1;
  const mediaKey = currentItem
    ? `${getBrandIdentity(currentItem)}-${mediaVersion}`
    : undefined;
  const currentVideoIdentity = getBrandIdentity(currentItem);
  // SSR 仅在服务端已确认桌面端时输出首帧；客户端确认视口后再按真实断点决定。
  const canRenderPcVideoMedia =
    !isMobile && (isMediaResolved || enableServerVideoPoster);
  const currentVideoPoster =
    canRenderPcVideoMedia && currentItem && isVideoItem(currentItem)
      ? currentItem.videoPoster?.trim() || ''
      : '';
  const isCurrentVideoDisplayReady =
    !currentVideoPoster ||
    (mediaKey ? videoDisplayReadyKey === mediaKey : false);
  const shouldRenderCurrentVideoPoster = Boolean(currentVideoPoster);
  const isOverseasPc = site === 'en' && !isMobile;
  const scrollClassName = [
    styles.scroll,
    site === 'en' ? styles.enScroll : '',
    isOverseasPc ? styles.overseasPc : '',
    hasBrandContent || hasMobileBrandImages
      ? styles.brandMode
      : styles.themeMode,
  ].join(' ');
  const titleClassName = [
    styles.title,
    isOverseasPc ? styles.overseasPcTitle : '',
  ].join(' ');
  const subtitleClassName = [
    styles.subtitle,
    isOverseasPc ? styles.overseasPcSubtitle : '',
  ].join(' ');
  const infoClassName = [
    styles.info,
    !hasButtons ? styles.infoNoButtons : '',
    isOverseasPc ? styles.overseasPcInfo : '',
  ].join(' ');
  const buttonGroupClassName = [
    styles.buttonGroup,
    !hasButtons ? styles.buttonGroupNoButtons : '',
  ].join(' ');
  const infoItemClassName = [
    styles.infoItem,
    isOverseasPc ? styles.overseasPcInfoItem : '',
  ].join(' ');
  const infoDotClassName = [
    styles.infoDot,
    isOverseasPc ? styles.overseasPcInfoDot : '',
  ].join(' ');

  return (
    <div ref={scrollRef} className={scrollClassName}>
      <div className={styles.stage}>
        <div className={styles.canvas}>
          {/* 品牌营销轮播背景：移动端图片走自定义 fade 轮播（避开 TDesign Swiper 的 children 错位问题），PC 媒体沿用原播放逻辑 */}
          {hasMobileBrandImages ? (
            <div className={styles.brandMobileBackground}>
              <div className={styles.brandMobileSwiper}>
                {mobileSlides.map((item) => {
                  const id = getBrandIdentity(item);
                  return (
                    <img
                      key={id}
                      ref={(el) => {
                        mobileSlideImgRefs.current.set(id, el);
                      }}
                      src={getConferenceMobileBrandImageUrl(item.contentUrl)}
                      className={styles.brandMobileSwiperImage}
                      alt=""
                      style={{ opacity: id === mobileActiveIdentity ? 1 : 0 }}
                    />
                  );
                })}
              </div>
            </div>
          ) : null}

          {currentItem && isVideoItem(currentItem) && canRenderPcVideoMedia ? (
            <>
              {shouldRenderCurrentVideoPoster ? (
                <img
                  src={currentVideoPoster}
                  className={`${styles.bannerBgPoster} ${
                    isCurrentVideoDisplayReady ? styles.bannerBgMediaHidden : ''
                  }`}
                  alt=""
                />
              ) : null}
              {displayBrands.map((item) => {
                const identity = getBrandIdentity(item);
                const isCurrentVideo = identity === currentVideoIdentity;
                const hasSource = videoSourceIds.includes(identity);
                return (
                  <video
                    key={identity}
                    ref={(element) => {
                      videoPoolRefs.current.set(identity, element);
                    }}
                    src={hasSource ? item.contentUrl : undefined}
                    className={`${styles.bannerBgMedia} ${
                      isCurrentVideo && isCurrentVideoDisplayReady
                        ? ''
                        : styles.bannerBgMediaHidden
                    }`}
                    poster={
                      isCurrentVideo
                        ? currentVideoPoster || undefined
                        : undefined
                    }
                    muted
                    playsInline
                    preload={
                      isCurrentVideo || identity === nextVideoIdentity
                        ? 'auto'
                        : 'none'
                    }
                    loop={isCurrentVideo && isSingleBrand}
                    onCanPlay={() => handlePooledVideoCanPlay(identity)}
                    onError={() => handlePooledVideoError(identity)}
                    onEnded={
                      isCurrentVideo ? () => onEndedRef.current() : undefined
                    }
                  />
                );
              })}
            </>
          ) : currentItem ? (
            <canvas
              key={mediaKey}
              ref={canvasRef}
              className={styles.bannerBgMedia}
            />
          ) : themeCoverStyle ? (
            <div
              className={styles.themeCoverFallback}
              style={themeCoverStyle}
            />
          ) : null}

          {languageSwitch ? (
            <div className={styles.languageSwitch}>{languageSwitch}</div>
          ) : null}

          {/* "AI for Good" 大标题（写死，初始展示） */}
          <h1 ref={headlineRef} className={styles.headline}>
            <span className={`${styles.headlineCn} ${styles.headlineOffset}`}>
              解法
            </span>
            <span>Seeking</span>
            <span className={styles.headlineOffset}>Solutions</span>
            <span className={styles.headlineCn}>2026</span>
          </h1>

          {/* 主题信息组（从屏外下方上移） */}
          <div ref={infoGroupRef} className={styles.infoGroup}>
            <h2 className={titleClassName}>
              {titleLines.length > 1 ? (
                <>
                  {titleLines[0]}
                  <br />
                  {titleLines[1]}
                </>
              ) : (
                titleLines[0]
              )}
            </h2>

            <p className={subtitleClassName}>
              {isZh ? subtitleCN : subtitleEN}
            </p>

            {showEventDate || showAddress ? (
              <div className={infoClassName}>
                {showEventDate ? (
                  <div className={infoItemClassName}>
                    <img
                      src={iconTime.src}
                      className={styles.timeIcon}
                      width={timeIconSize}
                      height={timeIconSize}
                      alt=""
                    />
                    <span>{formatDate(eventDate)}</span>
                  </div>
                ) : null}
                {showEventDate && showAddress ? (
                  <span className={infoDotClassName} />
                ) : null}
                {showAddress ? (
                  <div className={infoItemClassName}>
                    <img
                      src={iconAddress.src}
                      className={styles.addressIcon}
                      width={addressIconWidth}
                      height={addressIconHeight}
                      alt=""
                      style={{ alignSelf: 'flex-start' }}
                    />
                    <span>{addressText}</span>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className={buttonGroupClassName}>
              {showPrimaryButton ? (
                <a
                  href={primaryButtonHref}
                  className={styles.primaryBtn}
                  target={primaryUsesControlledSignup ? undefined : '_blank'}
                  rel={
                    primaryUsesControlledSignup
                      ? undefined
                      : 'noopener noreferrer'
                  }
                  onClick={(event) =>
                    void handleConferenceSignupClick(
                      event,
                      'primary',
                      primaryBtnLink
                    )
                  }
                >
                  {primaryButtonText}
                </a>
              ) : null}
              {showSecondaryButton ? (
                <a
                  href={secondaryButtonHref}
                  className={styles.secondaryBtn}
                  target={secondaryUsesControlledSignup ? undefined : '_blank'}
                  rel={
                    secondaryUsesControlledSignup
                      ? undefined
                      : 'noopener noreferrer'
                  }
                  onClick={(event) =>
                    void handleConferenceSignupClick(
                      event,
                      'secondary',
                      secondaryBtnLink
                    )
                  }
                >
                  {secondaryButtonText}
                </a>
              ) : null}
            </div>
          </div>

          {/* SCROLL 提示 */}
          {/* <div ref={scrollHintRef} className={styles.scrollHint}>
            <span>SCROLL</span>
            <span className={styles.lineDown} />
          </div> */}
        </div>
      </div>
    </div>
  );
}
